import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    city: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Salva nel database (non bloccante: se fallisce, procediamo comunque con WhatsApp)
      try {
        await axios.post(`${API}/quotes`, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          city: formData.city,
          message: formData.message || ''
        });
      } catch (dbErr) {
        console.warn('Quote DB save failed, continuing with WhatsApp:', dbErr?.message);
      }

      // Invia via WhatsApp
      const serviceLabels = {
        'serrande': 'Serrande Avvolgibili',
        'porte-sezionali': 'Porte Sezionali',
        'tapparelle': 'Tapparelle',
        'cancelli': 'Cancelli',
        'automazioni': 'Automazioni',
        'manutenzione': 'Manutenzione'
      };
      
      const message = `*Richiesta Preventivo*

👤 *Nome:* ${formData.name}
📧 *Email:* ${formData.email}
📱 *Telefono:* ${formData.phone}
🛠️ *Servizio:* ${serviceLabels[formData.service] || formData.service}
🏙️ *Città:* ${formData.city}
${formData.message ? `\n💬 *Messaggio:*\n${formData.message}` : ''}`;

      const whatsappUrl = `https://wa.me/39337743730?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      setIsSubmitted(true);
      toast.success('Apertura WhatsApp... Invia il messaggio per completare la richiesta!');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          city: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Errore durante l\'invio. Riprova o contattaci telefonicamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="quote" className="py-20 bg-gradient-to-b from-slate-900 to-slate-800 text-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Info */}
          <div>
            <div className="inline-block mb-4">
              <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Preventivo Gratuito
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Richiedi un Preventivo Senza Impegno
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Compila il form e riceverai un preventivo dettagliato entro 24 ore. Il sopralluogo è sempre gratuito.
            </p>

            {/* Benefits */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-green-600 rounded-full p-1 mt-1">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Sopralluogo Gratuito</h4>
                  <p className="text-gray-400">Un nostro tecnico verrà a valutare le tue esigenze</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-green-600 rounded-full p-1 mt-1">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Preventivo Dettagliato</h4>
                  <p className="text-gray-400">Riceverai un preventivo completo e trasparente</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-green-600 rounded-full p-1 mt-1">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Nessun Obbligo</h4>
                  <p className="text-gray-400">Sei libero di valutare senza impegno</p>
                </div>
              </div>
            </div>

            {/* Italian Flag Accent */}
            <div className="flex gap-2 mt-8">
              <div className="w-24 h-2 bg-green-600 rounded-full"></div>
              <div className="w-24 h-2 bg-white rounded-full"></div>
              <div className="w-24 h-2 bg-red-600 rounded-full"></div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-green-600" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Richiesta Inviata!
                </h3>
                <p className="text-gray-600">
                  Ti contatteremo entro 24 ore per fissare un appuntamento.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome e Cognome *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow disabled:opacity-50"
                    placeholder="Mario Rossi"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow disabled:opacity-50"
                      placeholder="mario@email.it"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefono *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow disabled:opacity-50"
                      placeholder="333 1234567"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Servizio Richiesto *
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow disabled:opacity-50"
                    >
                      <option value="">Seleziona servizio</option>
                      <option value="serrande">Serrande Avvolgibili</option>
                      <option value="porte-sezionali">Porte Sezionali</option>
                      <option value="tapparelle">Tapparelle</option>
                      <option value="cancelli">Cancelli</option>
                      <option value="automazioni">Automazioni</option>
                      <option value="manutenzione">Manutenzione</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Città *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow disabled:opacity-50"
                      placeholder="Roma"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Messaggio
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow resize-none disabled:opacity-50"
                    placeholder="Descrivi brevemente le tue esigenze..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-green-600 text-white py-4 rounded-lg hover:bg-green-700 transition-all hover:shadow-lg font-semibold text-lg flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Invio in corso...' : (
                    <>
                      Invia via WhatsApp
                      <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  I tuoi dati sono protetti e verranno utilizzati solo per contattarti
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
